<template>
	<div v-click-outside="handleClickedOutside" class="autocomplete">
		<label
			for="autocomplete-container"
			class="autocomplete-container"
			@click.stop="isOpened = true;"
			@focus.stop="isOpened = true;"
		>
			<input type="hidden" id="autocomplete-container" />
			<BaseInput2
				v-if="selectedItems?.length == 0"
				:class="{
					'dropdown-search': (selectPlaceholderName?.length + selectName?.length) > 0
				}"
				v-model="selectedItem"
				:placeholder="selectPlaceholderName || placeholder"
				:prepend-inner="prependInner"
				:append-inner="true"
				:variant="variant"
				readonly
				@click.stop="isOpened = true;"
				@focus.stop="isOpened = true;"
			>
				<template #append-inner>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						class="cursor-pointer"
						@click.stop="() => { search = null; isOpened = !isOpened; }"
						style="margin-top: 3px;transition: transform .2s;"
						:style="{
							transform: isOpened ? 'rotateX(180deg)' : ''
						}"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
					>
						<path d="M19 9l-7 7-7-7"></path>
					</svg>
				</template>
			</BaseInput2>
			<v-autocomplete
				v-else
				v-model="selectedItems"
				:disabled="false"
				:hide-no-data="true"
				:hide-details="true"
				:hide-spin-buttons="true"
				hidden
				deletable-chips
				hide-selected
				:items="people ?? []"
				chips
				closable-chips
				multiple
				variant="none"
				:class="{
					'v-input-focused': isOpened
				}"
				close-on-content-click
				style="pointer-events: none;"
				@keydown.enter="goNextStep()"
			>
				<template v-slot:chip="{ props, item }">
					<v-chip
						v-bind="props"
						:text="item?.raw?.name"
						color="primary"
						text-color="white100"
						variant="elevated"
						closable
						@update:model-value="emit('update:modelValue', selectedItems.filter(_item => _item?.id != item?.raw?.id))"
						@keydown.enter="goNextStep()"
					/>
				</template>
				<template v-slot:append-inner>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						class="cursor-pointer next-hidden"
						@click.stop="() => { search = null; isOpened = !isOpened; }"
						style="margin-top: 3px;transition: transform .2s;"
						:style="{
							transform: isOpened ? 'rotateX(180deg)' : ''
						}"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
					>
						<path d="M19 9l-7 7-7-7"></path>
					</svg>
				</template>
			</v-autocomplete>
		</label>
		<div
			class="autocomplete__list-container transition-25s"
			:class="{
				'position-static': positionStatic,
				'dropdown-top': dropdownTop,
				'border-y-0 shadow-none': !isOpened,
				'hide-visible': hideDropdown,
			}"
		>
			<BaseInput2
				v-model="search"
				:placeholder="placeholder"
				:prepend-inner="prependInner"
				:append-inner="appendInner"
				:variant="variant"
				@click.stop="isOpened = true;"
				@focus.stop="isOpened = true;"
				v-if="dropdownSearch"
			>
				<template #append-inner>
					<slot name="append-inner" :resetSearchInput="() => { search = null; isOpened = false; }"></slot>
				</template>
			</BaseInput2>
			<div class="v-input__details text-red500" v-if="dropdownSearch && searchResults?.length == 0">
				<div
					class="v-messages"
					role="alert"
					aria-live="polite"
					id="input-2-messages"
				>
					<div class="v-messages__message">{{ $t('Invalid input. Select from the list') }}</div>
				</div>
			</div>
			<ul
				class="autocomplete__list transition-25s"
				:class="{
					'max-h-0': !isOpened
				}"
				v-if="searchResults?.length"
			>
				<li
					v-for="result in searchResults"
					:key="result?.id || result"
					:title="result?.name"
					class="caption-2-regular | autocomplete__list-item truncate"
					:class="{
						active: selectMode && selectedItemIds[result?.id]
					}"
					@click.stop="setSelected(result)"
				>
					{{ result?.name }}
				</li>
			</ul>
		</div>
	</div>
</template>

<script setup>
import { computed, ref, watch, defineProps, defineEmits, defineOptions } from 'vue'

defineOptions({
	name: 'BaseAutocomplete2'
})

const props = defineProps({
	items: {
		type: Array,
		required: true,
		default: () => [],
	},
	multiple: {
		type: Array,
		required: true,
		default: () => [],
	},
	modelValue: {
		type: [String, Number, Object],
	},
	variant: {
		type: String,
	},
	prependInner: {
		type: Boolean,
		default: false,
	},
	appendInner: {
		type: Boolean,
		default: false,
	},
	getNewName: {
		type: Boolean,
		default: false,
	},
	showAll: {
		type: Boolean,
		default: false,
	},
	dropdownSearch: {
		type: Boolean,
		default: false,
	},
	elasticSearch: {
		type: Boolean,
		default: false,
	},
	doneTypingInterval: {
		type: Number,
		default: 1000,
	},
	positionStatic: {
		type: Boolean,
		default: false,
	},
	dropdownTop: {
		type: Boolean,
		default: false,
	},
	selectMode: {
		type: Boolean,
		default: false,
	},
	defaultValue: {
		type: String,
		default: '',
	},
	selectName: {
		type: String,
		default: '',
	},
	selectPlaceholderName: {
		type: String,
		default: '',
	},
	placeholder: {
		type: String,
		default: 'Выбрать',
	},
})

const emit = defineEmits(['update:modelValue', 'update-items'])

const isOpened = ref(false);
const hideDropdownTimeout = ref(null);
const hideDropdown = ref(true);

function isObject(obj) {
	return (
		typeof obj === 'object' &&
		!Array.isArray(obj) &&
		obj !== null
	);
}

const selectedItem = ref(isObject(props.selectName) ? '' : props.selectName ?? '')
const selectedItems = ref(props.multiple?.length ? props.multiple : [])

const default_value = ref(isObject(props.defaultValue) ? '' : props.defaultValue ?? '');
const search = ref(isObject(props.defaultValue) ? '' : props.defaultValue ?? '')

watch(() => props.modelValue, (newValue, oldValue) => {
	if (!newValue) search.value = ''
})

function removeDefaultValue() {
	default_value.value = undefined;
}
const selectedItemIds = computed(() => {
	return selectedItems.value.reduce(
		(obj, item) => ({ ...obj, [item?.id]: true }), {}
	)
})
const searchResults = computed(() => {
	if (search.value != default_value.value) {
		removeDefaultValue()
	}
	if (search.value === '' || props.showAll || search.value === default_value.value || props.elasticSearch) {
		if (props.selectMode == true) {
			return props.items;
		}
		return props.items.filter(item => selectedItemIds.value[item?.id] != true);
	}

	return props.items.filter(item => {
		if (selectedItemIds.value[item?.id] == true && props.selectMode == false) return false;
		if (item.name.toLowerCase().includes((search.value ?? '')?.toLowerCase())) return item
	})
})

const setSelected = (item) => {
	if (props.selectMode) {
		let itemIndex = selectedItems.value.findIndex(_item => _item?.id == item?.id);
		if (itemIndex != -1) {
			selectedItems.value.splice(itemIndex, 1);
		}
		else {
			selectedItems.value.push(item);
		}
	}
	else {
		selectedItems.value.push(item);
	}

	if (selectedItems.value?.length == 0) {
		selectedItem.value = props.defaultValue
	}
	emit('update:modelValue', selectedItems.value)
}
const get_new_name = (value) => {
	search.value = value
	emit('update:modelValue', selectedItems.value)
}

const typingTimer = ref(null)

function updateItems(value) {

	clearTimeout(typingTimer.value);
	typingTimer.value = setTimeout(() => {
		emit('update-items', value?.trim?.() || value || '')
	}, props.doneTypingInterval);
}

watch(() => search.value, (newValue, oldValue) => {
	if (props.elasticSearch) {
		updateItems(newValue)
		return;
	}
	if (newValue === '') {
		isOpened.value = true;
		emit('update:modelValue', selectedItems.value)
	}
	else if (props.getNewName) get_new_name(newValue);
})

const handleClickedOutside = () => (isOpened.value = false)

watch(
	() => isOpened.value,
	(bool) => {
		if (bool) {
			hideDropdown.value = false;
			if (hideDropdownTimeout.value) clearTimeout(hideDropdownTimeout.value);
		}
		else {
			hideDropdownTimeout.value = setTimeout(() => {
				hideDropdown.value = true;
			}, 250);
		}
	}
);

</script>

<style scoped lang="scss">

.autocomplete {
	position: relative;

	&__list-container {
		position: absolute;
		top: calc(100% + 4px);

		&.dropdown-top {
			top: unset;
			bottom: calc(100% + 4px);
		}

		left: 4px;
		right: 4px;
		height: auto;
		max-height: 252px;
		overflow: auto;

		border: 8px solid #FFFFFF;
		border-radius: 5px;
		background: #FFFFFF;
		box-shadow: 0px 0px 15px 0px #99999933;

		z-index: 100;

		&::-webkit-scrollbar {
			display: none;
		}

		overflow: hidden;
	}

	&__list {
		width: 100%;
		height: auto;
		min-height: 0;
		max-height: 252px;
		overflow: auto;

		border: 8px solid #FFFFFF;
		border-left: none;
		border-right: none;
		border-radius: 5px;
		background: #FFFFFF;
		box-shadow: 0px 0px 15px 0px #99999933;

		z-index: 100;

		&::-webkit-scrollbar {
			width: 4px;
		}

		padding-bottom: 0 !important;
		border-bottom: 0;

		&-item {
			padding: 8px 20px;

			list-style: none;
			color: #00000080;
			transition: var(--base-transition);
			border-radius: 6px !important;

			&:hover {
				cursor: pointer;
				color: #000000;
				background: rgba(#00aea0, .1);
			}

			&.active {
				cursor: pointer;
				color: #000000;
				background: rgba(#00aea0, .1);
			}

			&:first-child {
				border-top-left-radius: 5px;
				border-top-right-radius: 5px;
			}

			margin-bottom: 2px;

			&:last-child {
				margin-bottom: 0;
				border-bottom-left-radius: 5px;
				border-bottom-right-radius: 5px;
			}
		}
	}
}

</style>

<style lang="scss">

.autocomplete {
	& .dropdown-search {
		& input.input {
			border-top: none !important;
			border-left: none !important;
			border-right: none !important;
			border-radius: 0px !important;
		}
	}

	.autocomplete__list-container .autocomplete__list {
		outline: none !important;
	}
}

.position-static {
	position: static !important;
}

.next-hidden {
	width: 16px;
	height: 16px;
	pointer-events: all;

	&+.mdi-menu-down {
		display: none;
	}
}

.autocomplete-container {
	.v-input {
		border-bottom: 1px solid #C8C8E3;
		min-height: 44px;
	}

	.v-field__input {
		align-items: center;
		padding: 10px 0px 10px 10px;
		min-height: 44px;
	}
}

.v-input-focused {
	outline: none;
	border-bottom: 1px solid #00aea0 !important;
}

.v-autocomplete__selection .v-chip__close {
	pointer-events: all;
}

.max-h-0 {
	max-height: 0px !important;
}

.border-t-0 {
	border-top: 0px solid transparent !important;
}

.border-y-0 {
	border-top: 0px solid #FFFFFF !important;
	border-bottom: 0px solid #FFFFFF !important;
}

.shadow-none {
	box-shadow: 0px 0px 0px 0px #FFFFFF !important;
}

.transition-25s {
	transition: .25s all;
}

.hide-visible {
	pointer-events: none;
	opacity: 0;
}

</style>
